import * as React from 'react';
import { cn } from '../lib/utils';

export type SocialProvider = 'google' | 'facebook' | 'apple';

export interface SocialButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  provider: SocialProvider;
  loading?: boolean;
}

const PROVIDER_LABEL: Record<SocialProvider, string> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};

// Le libellé visible est réduit au nom du provider (place gagnée en 2 colonnes),
// mais le nom accessible reste une phrase d'action complète.
const PROVIDER_ARIA_LABEL: Record<SocialProvider, string> = {
  google: 'Continuer avec Google',
  facebook: 'Continuer avec Facebook',
  apple: 'Continuer avec Apple',
};

function ProviderIcon({ provider }: { provider: SocialProvider }) {
  if (provider === 'google') {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    );
  }
  if (provider === 'facebook') {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
        <path
          fill="#1877F2"
          d="M24 12c0-6.63-5.37-12-12-12S0 5.37 0 12c0 5.99 4.39 10.95 10.13 11.85V15.47H7.08V12h3.05V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.69.23 2.69.23v2.95h-1.51c-1.49 0-1.96.93-1.96 1.88V12h3.33l-.53 3.47h-2.8v8.38C19.61 22.95 24 17.99 24 12z"
        />
      </svg>
    );
  }
  // apple
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
      <path
        fill="currentColor"
        d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
      />
    </svg>
  );
}

export const SocialButton = React.forwardRef<HTMLButtonElement, SocialButtonProps>(
  ({ provider, loading, className, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      aria-label={children ? undefined : PROVIDER_ARIA_LABEL[provider]}
      className={cn(
        // Base commune aux 3 providers — MÊME fond blanc, MÊME bordure, MÊME
        // taille : les boutons doivent se lire comme des options de connexion
        // équivalentes (Apple HIG « Sign in with Apple », guideline 4 —
        // rejet App Review 2026-07-07 : le variant noir précédent, unique
        // parmi 3 boutons blancs, ne se lisait pas comme un bouton de connexion
        // à part entière). Seule l'icône change de provider en provider.
        'inline-flex h-11 w-full items-center justify-center gap-2',
        'border-ink-200 rounded-[10px] border bg-white px-3',
        'text-ink-900 text-[15px] font-semibold',
        'hover:border-ink-300 hover:bg-ink-50 transition-all duration-150',
        'focus-visible:ring-primary-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {loading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <ProviderIcon provider={provider} />
      )}
      {children ?? PROVIDER_LABEL[provider]}
    </button>
  ),
);

SocialButton.displayName = 'SocialButton';
